import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, LinearProgress, Tab, Tabs,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Inventory as InventoryIcon,
  Warning as WarningIcon, ShoppingCart as ShoppingCartIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const emptyItem = {
  name: '', description: '', category: '', sku: '', unit: 'unidade',
  current_stock: 0, minimum_stock: 0, maximum_stock: 100,
  cost_price: 0, selling_price: 0, supplier: '',
  location: '', expiration_date: '', batch_number: '',
};

const categories = [
  'Material de Consumo', 'Material Descartável', 'Medicamento', 'Instrumental',
  'Equipamento', 'Material de Limpeza', 'Material de Escritório', 'EPI', 'Outros',
];

const Inventory: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(emptyItem);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (search) params.search = search;
      const res = await axios.get(getApiUrl('/clinic/inventory'), { params });
      if (res.data.success) setItems(res.data.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSave = async () => {
    const name = (formData.name || '').trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Preencha o nome do item.', severity: 'error' });
      return;
    }
    try {
      setSaving(true);
      const payload = { ...formData, name };
      if (editingId) {
        await axios.put(getApiUrl(`/clinic/inventory/${editingId}`), payload);
        setSnackbar({ open: true, message: 'Item atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/inventory'), payload);
        setSnackbar({ open: true, message: 'Item cadastrado!', severity: 'success' });
      }
      setDialogOpen(false); fetchItems();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir este item?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/inventory/${id}`));
      setSnackbar({ open: true, message: 'Item excluído!', severity: 'success' });
      fetchItems();
    } catch (err) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const lowStockItems = items.filter(i => i.current_stock <= i.minimum_stock && i.is_active);
  const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  const getStockLevel = (item: any) => {
    if (!item.maximum_stock) return 50;
    return Math.min(100, (item.current_stock / item.maximum_stock) * 100);
  };

  const getStockColor = (item: any) => {
    if (item.current_stock <= item.minimum_stock) return '#ef4444';
    if (item.current_stock <= item.minimum_stock * 1.5) return '#f59e0b';
    return '#10b981';
  };

  const filteredItems = tabValue === 0 ? items :
    tabValue === 1 ? lowStockItems :
    items.filter(i => !i.is_active);

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e1b4b' }}>
          Estoque & Materiais
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setFormData(emptyItem); setEditingId(null); setDialogOpen(true); }}
          sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
          Novo Item
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e9d5ff', bgcolor: '#faf5ff' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <InventoryIcon sx={{ color: '#8b5cf6' }} />
                <Typography variant="body2" color="text.secondary">Total de Itens</Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#7c3aed' }}>{items.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #fee2e2', bgcolor: '#fef2f2' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon sx={{ color: '#ef4444' }} />
                <Typography variant="body2" color="text.secondary">Estoque Baixo</Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#dc2626' }}>{lowStockItems.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #d1fae5', bgcolor: '#f0fdf4' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ShoppingCartIcon sx={{ color: '#10b981' }} />
                <Typography variant="body2" color="text.secondary">Valor em Estoque</Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#059669' }}>
                {formatCurrency(items.reduce((s, i) => s + (i.current_stock * Number(i.cost_price || 0)), 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', bgcolor: '#f9fafb' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CategoryIcon sx={{ color: '#6b7280' }} />
                <Typography variant="body2" color="text.secondary">Categorias</Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#374151' }}>
                {new Set(items.map(i => i.category).filter(Boolean)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {lowStockItems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          ⚠️ <strong>{lowStockItems.length} ite{lowStockItems.length === 1 ? 'm' : 'ns'}</strong> com estoque abaixo do mínimo:{' '}
          {lowStockItems.slice(0, 3).map(i => i.name).join(', ')}{lowStockItems.length > 3 ? '...' : ''}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Buscar item..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          sx={{ minWidth: 250, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        <Tooltip title="Atualizar"><IconButton onClick={fetchItems}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
        <Tab label={`Todos (${items.length})`} />
        <Tab label={`Estoque Baixo (${lowStockItems.length})`} />
        <Tab label={`Inativos (${items.filter(i => !i.is_active).length})`} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estoque</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Nível</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Custo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Venda</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fornecedor</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow key={item.id} hover sx={{ bgcolor: item.current_stock <= item.minimum_stock ? '#fef2f2' : 'inherit' }}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                      {item.sku && <Typography variant="caption" sx={{ color: '#9ca3af' }}>SKU: {item.sku}</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={item.category || 'N/A'} size="small" sx={{ bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 600 }} /></TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {item.current_stock} {item.unit}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>Min: {item.minimum_stock}</Typography>
                  </TableCell>
                  <TableCell sx={{ width: 120 }}>
                    <LinearProgress variant="determinate" value={getStockLevel(item)}
                      sx={{ height: 6, borderRadius: 3, bgcolor: '#e5e7eb', '& .MuiLinearProgress-bar': { bgcolor: getStockColor(item), borderRadius: 3 } }} />
                  </TableCell>
                  <TableCell>{formatCurrency(Number(item.cost_price || 0))}</TableCell>
                  <TableCell>{formatCurrency(Number(item.selling_price || 0))}</TableCell>
                  <TableCell>{item.supplier || '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => {
                      setFormData({
                        ...emptyItem,
                        ...item,
                        batch_number: item.lot_number ?? item.batch_number ?? '',
                        location: item.storage_location ?? item.location ?? '',
                        category: item.clinic_inventory_categories?.name ?? item.category ?? '',
                        supplier: item.clinic_suppliers?.company_name ?? item.supplier ?? '',
                      });
                      setEditingId(item.id);
                      setDialogOpen(true);
                    }}>
                      <EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredItems.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: '#9ca3af' }}>
                  <InventoryIcon sx={{ fontSize: 36, opacity: 0.5 }} /><Typography>Nenhum item encontrado</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          {editingId ? 'Editar Item' : 'Novo Item'}
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth required label="Nome do Item *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Categoria</InputLabel>
                <Select value={formData.category} label="Categoria" onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                  {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Descrição" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} size="small" multiline rows={2} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="SKU / Código" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Unidade</InputLabel>
                <Select value={formData.unit} label="Unidade" onChange={(e) => setFormData({ ...formData, unit: e.target.value })}>
                  <MenuItem value="unidade">Unidade</MenuItem><MenuItem value="caixa">Caixa</MenuItem>
                  <MenuItem value="pacote">Pacote</MenuItem><MenuItem value="litro">Litro</MenuItem>
                  <MenuItem value="kg">Kg</MenuItem><MenuItem value="ml">ml</MenuItem>
                  <MenuItem value="par">Par</MenuItem><MenuItem value="rolo">Rolo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Lote" value={formData.batch_number} onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Estoque Atual" type="number" value={formData.current_stock} onChange={(e) => setFormData({ ...formData, current_stock: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Estoque Mínimo" type="number" value={formData.minimum_stock} onChange={(e) => setFormData({ ...formData, minimum_stock: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Estoque Máximo" type="number" value={formData.maximum_stock} onChange={(e) => setFormData({ ...formData, maximum_stock: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Preço de Custo" type="number" value={formData.cost_price} onChange={(e) => setFormData({ ...formData, cost_price: Number(e.target.value) })} size="small"
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Preço de Venda" type="number" value={formData.selling_price} onChange={(e) => setFormData({ ...formData, selling_price: Number(e.target.value) })} size="small"
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Validade" type="date" value={formData.expiration_date} onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })} size="small" InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Fornecedor" value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Localização" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} size="small" placeholder="Ex: Armário A, Prateleira 3" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Inventory;
